import { useEffect, useRef } from 'react';
import { shouldRenderField } from '../lib/scroll';
import type { Block } from '../gl/field';

/**
 * The persistent growth field. Everything about it is additive: the page is
 * complete without it, and it is never created when the visitor has signalled
 * that they do not want it.
 */
export function Field() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !shouldRenderField()) return;

    let disposed = false;
    let field: import('../gl/field').GrowthField | null = null;
    const cleanups: (() => void)[] = [];

    const boot = async () => {
      const { GrowthField } = await import('../gl/field');
      if (disposed) return;

      const css = getComputedStyle(document.documentElement);
      const read = (name: string, fallback: [number, number, number]) => {
        const v = css.getPropertyValue(name).trim();
        return v ? oklchToRgb(v) ?? fallback : fallback;
      };

      const coarse = window.matchMedia('(pointer: coarse)').matches;
      try {
        field = new GrowthField(canvas, {
          bg: read('--bg', [0.051, 0.032, 0.024]),
          ember: read('--ember', [0.988, 0.557, 0.294]),
          chloro: read('--chloro', [0.663, 0.965, 0.412]),
          maxDpr: coarse ? 1.4 : 1.75,
          nodeBudget: coarse ? 1500 : 2600,
        });
      } catch {
        return; // no WebGL2, or the driver refused — the fallback layer stands in
      }
      if (disposed) {
        field.dispose();
        return;
      }

      const sections = Array.from(document.querySelectorAll<HTMLElement>('[data-act]')).map(
        (el) => ({
          el,
          act: Number(el.dataset.act),
          // Queried once: this runs on every scroll frame.
          cols: Array.from(el.querySelectorAll<HTMLElement>('.grid > *')),
        }),
      );

      const PAD = 40;

      /**
       * Every text column on screen right now, as a viewport-UV edge, the side
       * the light may fall on, and the rows it occupies. Measured rather than
       * assumed, so the field can never brighten a reading column at any width.
       *
       * The result is folded into exactly two blocks, one per direction, each
       * taking the strictest edge and the union of rows. That is conservative
       * by construction: it can only ever darken more than strictly necessary,
       * never less, whatever the page is doing.
       */
      const measureBlocks = (): [Block | null, Block | null] => {
        const W = window.innerWidth;
        const H = window.innerHeight;
        let right: Block | null = null;
        let left: Block | null = null;

        for (const { cols } of sections) {
          let l = Infinity;
          let r = -Infinity;
          let t = Infinity;
          let b = -Infinity;
          for (const c of cols) {
            const q = c.getBoundingClientRect();
            if (q.width < 8 || q.height < 8) continue;
            if (q.bottom < -PAD || q.top > H + PAD) continue;
            l = Math.min(l, q.left);
            r = Math.max(r, q.right);
            t = Math.min(t, q.top);
            b = Math.max(b, q.bottom);
          }
          if (!Number.isFinite(l)) continue;

          // GL's viewport UV runs bottom-up; the DOM's y runs top-down. Flip
          // here so the pair stays [lower, upper] in the shader's frame.
          const rows: [number, number] = [1 - (b + PAD) / H, 1 - (t - PAD) / H];
          // Type spanning most of the frame leaves only the outer margin lit.
          const toRight = (r - l) / W > 0.72 || (l + r) / 2 < W / 2;
          if (toRight) {
            const edge = (r + PAD) / W;
            right = right
              ? [Math.max(right[0], edge), Math.min(right[1], rows[0]), Math.max(right[2], rows[1])]
              : [edge, rows[0], rows[1]];
          } else {
            const edge = (l - PAD) / W;
            left = left
              ? [Math.min(left[0], edge), Math.min(left[1], rows[0]), Math.max(left[2], rows[1])]
              : [edge, rows[0], rows[1]];
          }
        }
        return [right, left];
      };

      let ticking = false;
      const measure = () => {
        ticking = false;
        if (!field || !sections.length) return;
        const play = window.scrollY + window.innerHeight * 0.42;
        let act = sections[0].act;
        for (let i = 0; i < sections.length; i++) {
          const a = sections[i];
          const top = a.el.offsetTop;
          const next = sections[i + 1];
          if (!next) {
            if (play >= top) act = a.act;
            continue;
          }
          const nTop = next.el.offsetTop;
          if (play >= top && play < nTop) {
            const t = (play - top) / Math.max(1, nTop - top);
            // Hold each act, then hand over across the last third of the section.
            const e = Math.max(0, Math.min(1, (t - 0.62) / 0.38));
            act = a.act + (next.act - a.act) * (e * e * (3 - 2 * e));
            break;
          }
        }
        field.setAct(act);
        const [right, left] = measureBlocks();
        field.setBlocks(right, left);
      };
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(measure);
      };

      const onResize = () => {
        field?.resize();
        onScroll();
      };
      const onPointer = (e: PointerEvent) => {
        field?.setPointer(
          (e.clientX / window.innerWidth) * 2 - 1,
          (e.clientY / window.innerHeight) * 2 - 1,
        );
      };
      const onVisibility = () => field?.setVisible(!document.hidden);

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onResize);
      document.addEventListener('visibilitychange', onVisibility);
      if (!coarse) window.addEventListener('pointermove', onPointer, { passive: true });
      cleanups.push(
        () => window.removeEventListener('scroll', onScroll),
        () => window.removeEventListener('resize', onResize),
        () => document.removeEventListener('visibilitychange', onVisibility),
        () => window.removeEventListener('pointermove', onPointer),
      );

      measure();
      field.start();
      canvas.dataset.live = 'true';
    };

    // Off the critical path: the first paint never waits on graph generation.
    const hasIdle = typeof window.requestIdleCallback === 'function';
    const idle = hasIdle
      ? window.requestIdleCallback(() => void boot(), { timeout: 1200 })
      : (setTimeout(() => void boot(), 60) as unknown as number);

    return () => {
      disposed = true;
      if (hasIdle) window.cancelIdleCallback(idle);
      else clearTimeout(idle);
      for (const c of cleanups) c();
      field?.dispose();
    };
  }, []);

  return (
    <>
      <div className="field-fallback" aria-hidden="true" />
      <canvas ref={ref} className="field" aria-hidden="true" />
    </>
  );
}

/**
 * Resolve an `oklch(L C H)` token to linear-ish sRGB for the shader. Browsers
 * do not hand back computed OKLCH as RGB, so convert it here rather than
 * duplicating the palette in two places.
 */
function oklchToRgb(value: string): [number, number, number] | null {
  const m = value.match(
    /oklch\(\s*([\d.]+%?)\s+([\d.]+%?)\s+([\d.]+)(?:deg)?\s*\)/i,
  );
  if (!m) return null;
  const num = (s: string, scale: number) =>
    s.endsWith('%') ? (parseFloat(s) / 100) * scale : parseFloat(s);
  const L = num(m[1], 1);
  const C = num(m[2], 0.4);
  const h = (parseFloat(m[3]) * Math.PI) / 180;
  const a = C * Math.cos(h);
  const b = C * Math.sin(h);
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const mm = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const lin = [
    4.0767416621 * l - 3.3077115913 * mm + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * mm - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * mm + 1.707614701 * s,
  ];
  const enc = (u: number) => {
    const v = Math.max(0, u); // negative values are out of gamut, not imaginary
    return Math.min(1, v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055);
  };
  return [enc(lin[0]), enc(lin[1]), enc(lin[2])];
}
